import { ClientPb, ServerPb, TypesPb } from 'fifty-seven-llc-protocol';
import { Logger } from './logger';
import { EventEmitter } from './event-emitter';

const Types = {};
Object.assign(Types, TypesPb);

const Client = {};
Object.assign(Client, ClientPb);

const Server = {};
Object.assign(Server, ServerPb);

function genJenkinsHash(data) {
  let buffer = new Array(data.length);
  for (let i = 0; i < data.length; i++) buffer[i] = data.charCodeAt(i);

  let h = 0;
  for (let i = 0; i < buffer.length; i++) {
    h = (h + buffer[i]) >>> 0;
    h = (h + (h << 10)) >>> 0;
    h = (h ^ (h >>> 6)) >>> 0;
  }
  h = (h + (h << 3)) >>> 0;
  h = (h ^ (h >>> 11)) >>> 0;
  h = (h + (h << 15)) >>> 0;

  return h;
}

export class ApiSocket extends EventEmitter {
  _logger = new Logger('ApiSocket');
  _packetsMap = new Map();
  _queue = [];
  _socket = null;
  _canSendData = false;
  _destroyed = false;
  _attempts = 0;

  constructor(url) {
    super();

    this._url = url;

    this._wrapPackets({ Types, Client, Server }, 'FiftySeven.Protocol');
    this.logger.trace('Initialized.');

    this._socketOnConnect = this._socketOnConnect.bind(this);
    this._socketOnDisconnect = this._socketOnDisconnect.bind(this);
    this._socketOnError = this._socketOnError.bind(this);
    this._socketOnMessage = this._socketOnMessage.bind(this);
  }

  destroy() {
    this._destroyed = true;
    this._socket.close(1000);
    this._packetsMap.clear();
    this.fire('destroyed');
    super.destroy();
    this.logger.trace('Destroyed.');
  }

  get logger() {
    return this._logger;
  }

  get url() {
    return this._url;
  }

  get destroyed() {
    return this._destroyed;
  }

  get connected() {
    return !!this._socket && this._socket.readyState === 1;
  }

  get canSendData() {
    return this._canSendData;
  }

  set canSendData(value) {
    this._canSendData = value;
    this._processQueue();
  }

  _wrapPackets(packetsObject, prefix = '') {
    Object.keys(packetsObject).forEach(key => {
      let path = (prefix ? prefix + '.' : '') + key;

      if (typeof packetsObject[key] === 'function' && key.charAt(0).match(/[A-Z]/)) {
        Object.defineProperty(packetsObject[key], 'name', { value: key });
        Object.defineProperty(packetsObject[key], 'path', { value: path });

        let hash = genJenkinsHash(path);
        this._packetsMap.set(hash, packetsObject[key]);

        this.logger.trace(`Packet ${hash} ${path} was registered.`);
      }

      this._wrapPackets(packetsObject[key], path);
    });
  }

  _processQueue() {
    if (!this.connected) return;

    if (!this.canSendData) return;

    const message = this._queue[0];
    if (!message) return;

    this._processPacket(message);

    this._queue.shift();
    setTimeout(() => this._processQueue(), 0);
  }

  _processPacket(message) {
    if (!this.connected) return;

    if (typeof message.serializeBinary !== 'function') return;

    const packet = new TypesPb.Packet();
    const packetId = [...this._packetsMap].find(i => i[1] === message['constructor'])?.[0];

    packet.setId(packetId);
    packet.setBuffer(message.serializeBinary());

    this._socket.send(packet.serializeBinary());
    this.logger.trace('Message', message['constructor'].path, 'was sent.');
  }

  _socketOnConnect(e) {
    this._attempts = 0;
    this._logger.trace('Connected to server.');
    this._processQueue();
    this.fire('connected', e);
  }

  _socketOnDisconnect(e) {
    this.fire('disconnected', e);
    this._logger.trace(`Disconnected from server with code ${e.code}.`);

    setTimeout(() => this.connect(), 1000 * Math.min(this._attempts, 10));
  }

  _socketOnError(e) {
    this.fire('error', e.data);

    if (!this.destroyed) this.connect();
  }

  _socketOnMessage(e) {
    const packet = TypesPb.Packet.deserializeBinary(e.data);
    const messageType = this._packetsMap.get(packet.getId());
    const message = messageType?.['deserializeBinary']?.(packet.getBuffer());
    this.logger.trace('Message', message['constructor'].path, 'was receive.');

    this.fire('message', message);
  }

  connect() {
    if (this.destroyed) return;

    if (this.connected) return;

    if (this._socket) {
      this._socket.removeEventListener('open', this._socketOnConnect);
      this._socket.removeEventListener('close', this._socketOnDisconnect);
      this._socket.removeEventListener('error', this._socketOnError);
      this._socket.removeEventListener('message', this._socketOnMessage);
      this._socket.close();
    }

    this._socket = new WebSocket(this.url);
    this._socket.binaryType = 'arraybuffer';
    this._socket.addEventListener('open', this._socketOnConnect);
    this._socket.addEventListener('close', this._socketOnDisconnect);
    this._socket.addEventListener('error', this._socketOnError);
    this._socket.addEventListener('message', this._socketOnMessage);

    this._attempts++;
    this.logger.trace('Connecting to server. Attempt:', this._attempts);
  }

  reconnect() {
    this._socket.close(1000);
  }

  send(message, bypassQueue = false) {
    if (!bypassQueue) {
      if (this._queue.push(message) === 1) this._processQueue();
    } else {
      this._processPacket(message);
    }
  }

  ping() {
    this.send(new ClientPb.Ping(), true);
  }
}

export const api = new ApiSocket(process.env.VUE_APP_WEB_SOCKET_URL);
